<template>
    <section v-if="data?.Series" class="flex flex-col text-base gap-4">
        <ManufacturerSeriesHeader />
        <ManufacturerSeriesCategories />
        <h2 class="container-fluid my-0">{{ $t("Global.Parts") }}</h2>
        <CategoryFilters />
        <CategoryParts :series="data?.Series" />
    </section>
</template>
<script setup lang="ts">
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { company, basePartNumber } = route.params;
const { $api } = useNuxtApp();

const manufacturer = ref<ManufacturerDetails | undefined>();
const category = ref<Category | undefined>();
const filterOptions = ref<FilterOption[]>([]);
const filterValues = ref<FilterValues>({ inStock: false, manufacturers: [], specs: [] });

type ProductSeriesResponse = {
    Series: ProductSeries;
    Manufacturer: ManufacturerDetails;
    Category: Category;
    IsUndesirableSearchTerm: boolean;
};

const { data, error } = await useApi<ProductSeriesResponse>("product-series/one", {
    query: { virtualPath: company, basePartNumber },
});

if (error.value) showError(error.value);

if (data.value) {
    category.value = data.value.Category;
    manufacturer.value = data.value.Manufacturer;
    const mfrPath = data.value.Manufacturer.VirtualPath;

    if (route.params.company != mfrPath) {
        await router.replace({ params: { company: mfrPath }, query: route.query });
    }

    useCanonicalUrl(router.resolve({ name: route.name!, params: { ...route.params, company: mfrPath } }).fullPath);

    filterOptions.value = await $api<FilterOption[]>("product-series/filters", {
        query: { productSeriesId: data.value.Series.Id, categoryId: data.value.Category.Id },
    });

    filterValues.value = getCategoryFilterValues(filterOptions.value, route.query);

    if (data.value.IsUndesirableSearchTerm) {
        useHead({
            meta: [{ name: "robots", content: "noindex" }],
        });
    }
}

const title = computed(() => t("ProductSeries.TitleFormat", [manufacturer.value?.Name, basePartNumber]));
const description = computed(() =>
    t("ProductSeries.MetaDescriptionFormat", [manufacturer.value?.Name, basePartNumber])
);

useSeoMeta({ title, ogTitle: title, description, ogDescription: description });

provide(CategoryInjectKey, { category, filterOptions, filterValues });
provide(ManufacturerInjectKey, { manufacturer });
</script>
